/**
 * Theme Toggle Component
 * 
 * Theme toggle component for switching between light and dark modes with:
 * - Elegant switch for theme toggling
 * - Preference saving in localStorage
 * - Appropriate icons (sun/moon)
 * - Smooth animations
 * - Multiple variants (switch, icon)
 * 
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.darkMode - Whether dark mode is enabled
 * @param {Function} props.onToggle - Callback when theme is toggled
 * @param {string} [props.variant='switch'] - Component variant ('switch' or 'icon')
 * @returns {JSX.Element} The theme toggle component
 */
import React from 'react';
import { 
  IconButton, 
  Tooltip, 
  Switch, 
  FormControlLabel, 
  Box,
  useTheme 
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Brightness4 as DarkModeIcon, 
  Brightness7 as LightModeIcon 
} from '@mui/icons-material';

const ThemeToggle = ({ darkMode, onToggle, variant = 'switch' }) => {
  const { t } = useTranslation();

  const theme = useTheme();

  if (variant === 'icon') {
    return (
      <Tooltip title={darkMode ? t('common.lightMode', 'Light Mode') : t('common.darkMode', 'Dark Mode')}>
        <IconButton 
          onClick={onToggle}
          color="inherit"
          sx={{
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'scale(1.1)',
            }
          }}
        >
          {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <LightModeIcon 
        sx={{ 
          color: !darkMode ? theme.palette.primary.main : theme.palette.text.disabled,
          transition: 'color 0.3s ease'
        }} 
      />
      <Switch
        checked={darkMode}
        onChange={onToggle}
        color="primary"
        sx={{
          '& .MuiSwitch-thumb': {
            transition: 'all 0.3s ease',
          },
          '& .MuiSwitch-track': {
            transition: 'all 0.3s ease',
          }
        }}
      />
      <DarkModeIcon 
        sx={{ 
          color: darkMode ? theme.palette.primary.main : theme.palette.text.disabled,
          transition: 'color 0.3s ease'
        }} 
      />
    </Box>
  );
};

export default React.memo(ThemeToggle);

